import { PlusIcon } from "@xola/icons";
import { Button, Popover, PopoverList } from "@xola/ui-kit";
import clsx from "clsx";
import React, { useState } from "react";
import { Permission } from "types";
import { IsGranted } from "@/components/Common/IsGranted";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { SearchInput } from "@/components/Common/SearchInput";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useSearch } from "@/hooks/useSearch";
import { useVisibility } from "@/hooks/useVisibility";

export interface Language {
    locale: string;
    language: string;
}

interface LanguagesDropdownProps {
    languages: Language[];
    onAdd: (language: Language) => void;
}

export const LanguagesDropdown = ({ languages, onAdd }: LanguagesDropdownProps) => {
    const [visible, { toggle, hide }] = useVisibility(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selected, setSelected] = useState<Language | null>(null);

    const filteredLanguages = useSearch(languages, (language) => [language.language], searchTerm);

    const hideAndReset = () => {
        hide();

        setTimeout(() => {
            setSearchTerm("");
            setSelected(null);
        }, 75);
    };

    const handleAdd = () => {
        onAdd(selected!);
        hideAndReset();
    };

    return (
        <Popover
            visible={visible}
            maxWidth={450}
            offset={[0, 18]}
            placement="bottom"
            trigger="click"
            onClickOutside={hideAndReset}
        >
            <IsGranted permissions={Permission.MULTI_LANGUAGE_SUPPORT_ADD_LANGUAGE}>
                <SecondaryButton size="small" icon={<PlusIcon />} data-testid="add-language-button" onClick={toggle}>
                    Add Language
                </SecondaryButton>
            </IsGranted>

            <Popover.Content className="min-w-100">
                <div className="p-4">
                    <SearchInput onChange={setSearchTerm} />
                </div>

                {filteredLanguages.length > 0 ? (
                    <div className="flex max-h-75 flex-col overflow-y-auto">
                        {filteredLanguages.map((language, index) => (
                            <PopoverList.Item
                                key={language.locale}
                                className={clsx(
                                    "!rounded-none border-b border-gray-light capitalize",
                                    { "border-t": index === 0 },
                                    { "!bg-primary-lighter": selected?.locale === language.locale },
                                )}
                                onClickItem={() => setSelected(language)}
                            >
                                <NoTranslate>{language.language}</NoTranslate>
                            </PopoverList.Item>
                        ))}
                    </div>
                ) : (
                    <div className="px-4 py-2 text-center">No results found</div>
                )}

                <div className="flex justify-end space-x-2 p-4">
                    <SecondaryButton onClick={hideAndReset}>Cancel</SecondaryButton>
                    <Button disabled={!selected} size="medium" type="button" onClick={handleAdd}>
                        Add
                    </Button>
                </div>
            </Popover.Content>
        </Popover>
    );
};
